import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { toast } from 'sonner';

export interface NotificationPreferences {
  marking_fuel_enabled: boolean;
  marking_odometer_enabled: boolean;
  gps_webhook_enabled: boolean;
  gps_email_enabled: boolean;
  gps_telegram_enabled: boolean;
  system_announcements_enabled: boolean;
  system_warnings_enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
  expenses_due_soon_enabled: boolean;
  expenses_overdue_enabled: boolean;
  expenses_due_days_before: number;
}

export function useNotificationPreferences(companyId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // Fetch preferences
  const { data: preferences, isLoading } = useQuery({
    queryKey: ['notification-preferences', companyId, user?.id],
    queryFn: async () => {
      if (!companyId || !user?.id) return null;
      
      const { data, error } = await (supabase as any)
        .from('fleet_notification_preferences')
        .select('*')
        .eq('company_id', companyId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      // Create default if doesn't exist
      if (!data) {
        const { data: newPref, error: insertError } = await (supabase as any)
          .from('fleet_notification_preferences')
          .insert({
            company_id: companyId,
            user_id: user.id,
            expenses_due_soon_enabled: true,
            expenses_overdue_enabled: true,
            expenses_due_days_before: 3,
          })
          .select()
          .single();
        
        if (insertError) throw insertError;
        return newPref as NotificationPreferences;
      }
      
      if (error) throw error;
      return data as NotificationPreferences;
    },
    enabled: !!companyId && !!user?.id,
  });
  
  // Update preferences
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      if (!companyId || !user?.id) throw new Error('Missing required data');
      
      const { data, error } = await (supabase as any)
        .from('fleet_notification_preferences')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('company_id', companyId)
        .eq('user_id', user.id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success('Preferências atualizadas');
    },
    onError: () => {
      toast.error('Erro ao atualizar preferências');
    },
  });
  
  return {
    preferences,
    isLoading,
    updatePreferences: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
}
